import React from "react";
import { AlertTriangle, DollarSign } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface CreatorOnboardingModalProps {
  posit: {
    id: string;
    title: string;
    subscription_price?: number | null;
    creator_name?: string;
    creator_handle?: string;
    creator_first_name?: string;
  };
  onCancel: () => void;
}

const CreatorOnboardingModal: React.FC<CreatorOnboardingModalProps> = ({ posit, onCancel }) => {
  const subscriptionPrice = posit.subscription_price || 5.00;
  const creatorName = posit.creator_name || posit.creator_first_name || posit.creator_handle || 'Creator';

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2
    }).format(price);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-center">Creator Setup Required</DialogTitle>
          <DialogDescription className="text-center">
            This creator hasn't completed their payout setup yet
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Posit Info Card */}
          <Card>
            <CardContent className="p-4">
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg leading-tight">{posit.title}</h3>
                <p className="text-sm text-muted-foreground">
                  by {creatorName}
                </p>
                <div className="text-2xl font-bold">
                  {formatPrice(subscriptionPrice)}
                  <span className="text-lg font-normal text-muted-foreground">/month</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Message */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <DollarSign className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  Payout Setup Pending
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-200">
                  The creator needs to complete their Stripe Connect onboarding to receive payments. 
                  This is required for revenue splitting between the creator and platform.
                </p>
              </div>
            </div>
          </div>

          {/* What this means */}
          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="font-medium">What happens when they complete setup:</p>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              <span>You can subscribe to this Posit</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              <span>Creator receives 90% of subscription revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              <span>Platform takes 10% to maintain the service</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} className="flex-1">
              Maybe Later
            </Button>
            <Button 
              onClick={() => {
                // Navigate to creator profile or public posits
                if (posit.creator_handle) {
                  window.location.href = `/p/${posit.creator_handle}`;
                } else {
                  window.location.href = '/public-posits';
                }
              }}
              className="flex-1"
            >
              Browse Other Posits
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatorOnboardingModal; 